import { useEffect, useRef, useState } from 'react'

import type { DevelopmentTaskMagicLinkResolved, DevelopmentTaskMaintainerResolved } from '../../app/development-tasks'
import { resolveDevelopmentTaskMagicLink, resolveDevelopmentTaskMaintainerTask } from '../../app/development-tasks'
import { Card, CardHint, CardTitle } from '../../components/card'
import { t } from '../../copy'

type Phase = 'opening' | 'ready' | 'error'

export function PublicDevelopmentTaskLinkScreen() {
  const started = useRef(false)
  const [phase, setPhase] = useState<Phase>('opening')
  const [resolved, setResolved] = useState<DevelopmentTaskMagicLinkResolved | DevelopmentTaskMaintainerResolved | null>(null)
  const [credentialMode, setCredentialMode] = useState<'one_time' | 'maintainer'>('one_time')
  const [error, setError] = useState('')

  useEffect(() => {
    if (started.current) return
    started.current = true
    let raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : ''
    try { raw = decodeURIComponent(raw) } catch { raw = '' }
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
    if (!raw) {
      setError(t('development_task.magic.token_missing'))
      setPhase('error')
      return
    }

    let request: Promise<DevelopmentTaskMagicLinkResolved | DevelopmentTaskMaintainerResolved>
    if (raw.startsWith('maintainer.')) {
      try {
        const encoded = raw.slice('maintainer.'.length).replaceAll('-', '+').replaceAll('_', '/')
        const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '='))) as { v: number; task_id: string; key: string }
        if (payload.v !== 1 || !payload.task_id || !payload.key) throw new Error('invalid')
        setCredentialMode('maintainer')
        request = resolveDevelopmentTaskMaintainerTask(payload.task_id, payload.key)
      } catch {
        setError(t('development_task.magic.token_missing'))
        setPhase('error')
        return
      }
    } else {
      request = resolveDevelopmentTaskMagicLink(raw)
    }

    void request
      .then((value) => { setResolved(value); setPhase('ready') })
      .catch((cause) => { setError(cause instanceof Error ? cause.message : t('development_task.magic.open_error')); setPhase('error') })
  }, [])

  const packet = resolved?.task_packet
  return (
    <main className="min-h-screen bg-[linear-gradient(135deg,var(--color-npc-surface-warm)_0%,var(--color-npc-bg)_52%,var(--color-npc-blue-bg)_100%)] px-4 py-10 text-npc-ink sm:px-8 sm:py-16">
      <div className="mx-auto grid w-full max-w-[920px] gap-npc-grid-lg">
        <header className="max-w-[760px]">
          <div className="font-npc-mono text-npc-caption font-semibold uppercase text-npc-blue">NPC · MACHINE TASK</div>
          <h1 className="mb-0 mt-npc-gap-lg text-[clamp(2rem,6vw,4.5rem)] font-semibold leading-[0.96]">{t('development_task.magic.public.title')}</h1>
          <p className="mb-0 mt-npc-card max-w-[680px] text-npc-body leading-[1.7] text-npc-ink-soft">{t(credentialMode === 'maintainer' ? 'development_task.maintainer.public.hint' : 'development_task.magic.public.hint')}</p>
        </header>

        {phase === 'opening' ? <Card><CardTitle>{t('development_task.magic.opening')}</CardTitle></Card> : null}
        {packet ? (
          <Card data-testid="public-development-task-packet" className="grid gap-npc-card">
            <div><CardTitle>{packet.title}</CardTitle><CardHint className="mt-npc-gap-sm font-npc-mono">{packet.repo} · {packet.base_sha.slice(0, 12)} → {packet.target_ref}</CardHint></div>
            <div><div className="text-npc-caption font-semibold">{t('development_task.magic.prompt')}</div><p className="mb-0 mt-npc-gap-sm whitespace-pre-wrap text-npc-body-sm leading-[1.7] text-npc-ink-soft">{packet.instructions}</p></div>
            <div><div className="text-npc-caption font-semibold">{t('development_task.magic.acceptance')}</div><ol className="mb-0 mt-npc-gap-sm grid gap-npc-gap-xs pl-npc-grid-lg text-npc-body-sm leading-[1.65] text-npc-ink-soft">{packet.acceptance_criteria.map((item, index) => <li key={index}>{item.text}</li>)}</ol></div>
          </Card>
        ) : null}

        {phase === 'ready' && packet ? (
          <Card data-testid="public-development-task-machine" className="grid gap-npc-card">
            <div><CardTitle>{t('development_task.magic.identity.title')}</CardTitle><CardHint className="mt-npc-gap-sm leading-[1.6]">{t('development_task.magic.identity.hint')}</CardHint></div>
            <pre className="m-0 overflow-x-auto border-l-2 border-npc-blue bg-npc-surface p-npc-grid-sm font-npc-mono text-npc-meta leading-[1.6]">printf '%s' "$NPC_TASK_INVITE" | npc-agent task claim --executor &lt;stable-agent-id&gt;</pre>
            <p className="m-0 text-npc-body-sm leading-[1.6] text-npc-ink-soft">{t('development_task.magic.identity.capability')}</p>
          </Card>
        ) : null}

        {phase === 'error' ? <Card tone="amber"><CardTitle>{t('development_task.magic.error.title')}</CardTitle><p className="mb-0 mt-npc-gap-sm text-npc-body-sm text-npc-ink-soft">{error}</p></Card> : null}
      </div>
    </main>
  )
}
